import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import { SearchIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { PaywallDrawer } from '~/src/components/paywall'
import { Label } from '~/src/components/ui/label'
import { Switch } from '~/src/components/ui/switch'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import useHasProSubscription from '~/src/hooks/useHasProSubscription'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import { cn } from '~/src/lib/utils'
import { useProSearch, useStoreActions } from '~/src/stores/dupeInputStore'

interface Props {
  source: 'homepage' | 'results-top' | 'results-bottom'
  className?: string
  loadDelay?: number
  hideLabel?: boolean
  large?: boolean
  toastTitle?: string
  toastDescription?: string
}

const DupeInputProOptions: React.FC<Props> = ({
  className,
  loadDelay,
  hideLabel,
  large,
  toastTitle = 'Deep Search Enabled',
  toastDescription = 'Uses 2x more resources to find you more relevant results (beta feature)',
  source,
}) => {
  const analytics = useAnalytics()
  const { authUser } = useAuthUser()
  const { openAuthModal } = useAuthContext()
  const proSearchEnabled = useProSearch()
  const { toggleProSearch } = useStoreActions()
  const hasProSubscription = useHasProSubscription()

  const [isLoading, setIsLoading] = useState(false)
  const [showPaywall, setShowPaywall] = useState(false)

  const { featureOn: paywallDrawerFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.PaywallDrawer
  )

  const label = 'Deep search'

  const getRandomDelay = (delay?: number) => {
    if (!delay) return 0
    const minDelay = delay
    const maxDelay = delay * 2
    return Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay
  }

  const triggerToast = () => {
    toast.info(toastTitle, {
      description: toastDescription,
      classNames: {
        title: 'pro-gradient-text font-semibold',
        description: 'opacity-50',
      },
      position: 'bottom-right',
      icon: <SearchIcon className='h-4 w-4' />,
      duration: 5000,
    })
  }

  const enableProSearch = async (checked: boolean) => {
    if (loadDelay && checked) {
      setIsLoading(true)
      const randomDelay = getRandomDelay(loadDelay)
      await new Promise((resolve) => setTimeout(resolve, randomDelay))
      setIsLoading(false)
    }

    toggleProSearch(checked)

    if (checked) {
      analytics.track(AnalyticsEventName.DeepSearchEnabled, {
        source,
        loadDelay,
      })

      triggerToast()
    }
  }

  return (
    <>
      <Label
        htmlFor='dupe-pro-search'
        className={cn(
          'flex cursor-pointer items-center gap-2 whitespace-nowrap',
          className
        )}
      >
        <Switch
          id='dupe-pro-search'
          checked={proSearchEnabled}
          variant='pro'
          size={large ? 'md' : 'sm'}
          disabled={isLoading}
          loading={isLoading}
          onCheckedChange={async (checked) => {
            const callback = () => {
              if (!paywallDrawerFeatureEnabled) {
                enableProSearch(checked)
                return
              }

              if (hasProSubscription) {
                enableProSearch(checked)
              } else {
                setShowPaywall(true)
              }
            }

            if (!authUser?.uid) {
              openAuthModal({
                source: 'dupe-pro',
                modalSubtitle: 'Get access to our deep search feature',
                callback,
              })
            } else {
              callback()
            }
          }}
          className={cn('', {
            'pointer-events-none': !authUser?.uid,
          })}
        />
        {!hideLabel && (
          <div
            className={cn(
              'flex items-center text-black/50 transition-opacity group-hover:text-black/80'
            )}
          >
            <span className='text-sm font-medium'>{label}</span>
          </div>
        )}
      </Label>

      <PaywallDrawer
        isOpen={showPaywall}
        onOpenChange={setShowPaywall}
        source={`deep-search-${source}`}
        featureName='Deep Search'
        onSuccess={() => {
          enableProSearch(true)
        }}
      />
    </>
  )
}

export default DupeInputProOptions
